import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CreditCard, Loader2, Plus, Users, AlertTriangle } from "lucide-react";

const dollars = (cents) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

// Extract a human message from an axios error whose `detail` may be a string or {message}.
function detailMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || fallback;
  return d || fallback;
}

/**
 * Self-contained "Add a seat" button + confirmation modal.
 * Previews the exact prorated amount, charges the card on file, and only
 * completes when the invoice is PAID (backend rolls back on a declined card).
 *
 * Props:
 *   className  - styling for the trigger button
 *   label      - button text (default "Add seat ($25/mo)")
 *   onPurchased- called after a successful purchase (e.g. to refresh a list)
 */
export default function AddSeatButton({ className, label, onPurchased }) {
  const { user, checkAuth } = useAuth();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // null | {loading, proration_cents, monthly_cents, currency, error}

  const priceCents = user?.company_billing?.addon_seat_price_cents ?? 2500;
  const btnLabel = label ?? `Add seat (${dollars(priceCents)}/mo)`;

  const open = async () => {
    setConfirm({ loading: true });
    try {
      const { data } = await api.get("/company/addons/seats/preview", { params: { quantity: 1 } });
      setConfirm({ loading: false, error: null, ...data });
    } catch (e) {
      setConfirm(null);
      toast.error(detailMsg(e, "Could not calculate the price"));
    }
  };

  const submit = async () => {
    setBusy(true);
    setConfirm((c) => ({ ...c, error: null }));
    try {
      const { data } = await api.post("/company/addons/seats", { quantity: 1 });
      await checkAuth();
      const charged = data?.charged_cents ? ` — ${dollars(data.charged_cents)} charged today` : "";
      toast.success(`Extra seat added (${dollars(priceCents)}/mo)${charged}`);
      setConfirm(null);
      onPurchased?.();
    } catch (e) {
      setConfirm((c) => (c ? { ...c, error: detailMsg(e, "Could not add seat") } : c));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={open} disabled={busy || !!confirm} className={className}>
        {confirm?.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        {btnLabel}
      </button>

      {confirm && !confirm.loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => !busy && setConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-[#0025D8]" />
              <h3 className="font-display font-bold text-lg">Add an extra seat</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Your card on file will be charged now, then {dollars(confirm.monthly_cents ?? priceCents)}/mo going forward.
            </p>

            <div className="rounded-xl border border-border bg-secondary/40 divide-y divide-border mb-4">
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-muted-foreground">Charged today (prorated)</span>
                <span className="font-mono font-semibold text-sm">
                  {confirm.proration_cents >= 0 ? dollars(confirm.proration_cents) : "Prorated amount"}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-muted-foreground">Then monthly</span>
                <span className="font-mono font-semibold text-sm">{dollars(confirm.monthly_cents ?? priceCents)}/mo</span>
              </div>
            </div>

            {confirm.error && (
              <div className="flex items-start gap-2 rounded-lg bg-[#E11D48]/10 border border-[#E11D48]/30 px-3 py-2.5 mb-4">
                <AlertTriangle className="w-3.5 h-3.5 text-[#E11D48] flex-shrink-0 mt-0.5" />
                <div className="text-xs text-[#E11D48]">
                  {confirm.error}{" "}
                  <Link to="/company/billing" className="underline font-semibold" onClick={() => setConfirm(null)}>
                    Update card
                  </Link>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirm(null)} disabled={busy}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-60">
                Cancel
              </button>
              <button onClick={submit} disabled={busy}
                className="flex items-center gap-2 px-4 py-2 bg-[#0025D8] hover:bg-[#001CB3] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {confirm.error ? "Retry payment" : "Confirm & pay"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
